// =======================
// src/paginas/analise/autorizarCadastro/componentesPaginaVi/editarInfo.js
// =======================
import React, { useState, useEffect } from "react";
import { 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  IconButton, 
  Box,
  Divider
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import styled from "styled-components";

const colors = {
  primary: "#1976d2",
  secondary: "#6c5ce7",
  background: "var(--sear-superficie, #ffffff)",
  text: "var(--sear-texto, #2d3436)",
  error: "#d63031",
  success: "#00b894"
};

const StyledCard = styled(Card)`
  max-width: 700px;
  width: 100%;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: visible;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 6px;
    background: ${colors.primary};
    border-radius: 16px 16px 0 0;
  }
`;

const StyledTextField = styled(TextField)`
  & .MuiOutlinedInput-root {
    border-radius: 12px;
    transition: all 0.3s ease;
    
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${colors.primary};
    }
    
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${colors.primary};
      border-width: 2px;
    }
  }
`;

const ActionButton = styled(Button)`
  padding: 10px 24px;
  border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  
  &.save-button {
    background: ${colors.primary};
    &:hover {
      background: #1565c0;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
    }
  }
  
  &.cancel-button {
    border-color: ${colors.text};
    color: ${colors.text};
    &:hover {
      border-color: ${colors.primary};
      color: ${colors.primary};
      background: rgba(25, 118, 210, 0.04);
    }
  }
`;

const SectionTitle = styled(Typography)`
  margin: 24px 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid ${colors.primary};
  color: ${colors.primary};
  display: inline-block;
`;

const EdicaoInfo = ({ dados, onSave, onClose }) => {
  const { tipo } = useParams();
  const [formData, setFormData] = useState(dados);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nome) newErrors.nome = "Nome é obrigatório";
    if (!formData.email) newErrors.email = "Email é obrigatório";
    if (tipo === "mantenedora" && !formData.cnpj) newErrors.cnpj = "CNPJ é obrigatório";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  useEffect(() => {
    setFormData(dados);
  }, [dados]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      <StyledCard>
        <IconButton 
          sx={{ 
            position: "absolute", 
            top: 16, 
            right: 16,
            backgroundColor: "var(--sear-superficie-suave, rgba(0, 0, 0, 0.04))",
            '&:hover': {
              backgroundColor: "var(--sear-superficie-suave, rgba(0, 0, 0, 0.08))"
            }
          }} 
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>

        <CardContent sx={{ padding: 4 }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block'
            }}
          >
            {tipo === "escola" ? "Editar Escola" : "Editar Mantenedora"}
          </Typography>

          <SectionTitle variant="h6">
            Informações Básicas
          </SectionTitle>
          
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box sx={{ flex: "1 1 48%", minWidth: 250 }}>
              <StyledTextField
                label="Nome"
                name="nome"
                value={formData.nome || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
                error={!!errors.nome}
                helperText={errors.nome}
              />
            </Box>
            <Box sx={{ flex: "1 1 48%", minWidth: 250 }}>
              <StyledTextField
                label="Email"
                name="email"
                type="email"
                value={formData.email || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
                error={!!errors.email}
                helperText={errors.email}
              />
            </Box>

            {tipo === "escola" && (
              <>
                <Box sx={{ flex: "1 1 48%", minWidth: 250 }}>
                  <StyledTextField
                    label="Contato"
                    name="contato"
                    value={formData.contato || ""}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                  />
                </Box>
                <Box sx={{ flex: "1 1 48%", minWidth: 250 }}>
                  <StyledTextField
                    label="Município"
                    name="municipio"
                    value={formData.municipio || ""}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                  />
                </Box>
                <Box sx={{ flex: "1 1 48%", minWidth: 250 }}>
                  <StyledTextField
                    label="Modalidade"
                    name="modalidade"
                    value={formData.modalidade || ""}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                  />
                </Box>
              </>
            )}

            {tipo === "mantenedora" && (
              <>
                <Box sx={{ flex: "1 1 48%", minWidth: 250 }}>
                  <StyledTextField
                    label="Telefone"
                    name="telefone"
                    value={formData.telefone || ""}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                  />
                </Box>
                <Box sx={{ flex: "1 1 48%", minWidth: 250 }}>
                  <StyledTextField
                    label="Cidade"
                    name="cidade"
                    value={formData.cidade || ""}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                  />
                </Box>
                <Box sx={{ flex: "1 1 48%", minWidth: 250 }}>
                  <StyledTextField
                    label="Estado"
                    name="estado"
                    value={formData.estado || ""}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                  />
                </Box>
                <Box sx={{ flex: "1 1 48%", minWidth: 250 }}>
                  <StyledTextField
                    label="CNPJ"
                    name="cnpj"
                    value={formData.cnpj || ""}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    error={!!errors.cnpj}
                    helperText={errors.cnpj}
                  />
                </Box>
              </>
            )}
          </Box>

          <SectionTitle variant="h6">
            Dados de Endereço
          </SectionTitle>
          
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box sx={{ flex: "1 1 48%", minWidth: 250 }}>
              <StyledTextField
                label="Rua"
                name="rua"
                value={formData.rua || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Box>
            <Box sx={{ flex: "1 1 48%", minWidth: 250 }}>
              <StyledTextField
                label="Número"
                name="numero"
                value={formData.numero || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Box>
            <Box sx={{ flex: "1 1 48%", minWidth: 250 }}>
              <StyledTextField
                label="Bairro"
                name="bairro"
                value={formData.bairro || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Box>
            <Box sx={{ flex: "1 1 48%", minWidth: 250 }}>
              <StyledTextField
                label="CEP"
                name="cep"
                value={formData.cep || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Box>
          </Box>

          <Box sx={{ 
            display: "flex", 
            justifyContent: "flex-end", 
            gap: 2, 
            mt: 4,
            pt: 2,
            borderTop: `1px solid rgba(0, 0, 0, 0.12)`
          }}>
            <ActionButton 
              variant="outlined" 
              onClick={onClose}
              className="cancel-button"
            >
              Cancelar
            </ActionButton>
            <ActionButton 
              variant="contained" 
              onClick={handleSave}
              className="save-button"
            >
              Salvar Alterações
            </ActionButton>
          </Box>
        </CardContent>
      </StyledCard>
    </motion.div>
  );
};

export default EdicaoInfo;